<template>
  <footer class="relative w-full bg-white border-t border-gray-100">
    <!-- Subtle background elements (matching header style) -->
    <div class="absolute inset-0 z-0 overflow-hidden">
      <div class="absolute -bottom-10 -right-10 w-32 h-32 border border-gray-200 rounded-full" />
      <div class="absolute top-1/4 left-1/4 w-16 h-16 border border-gray-200 rotate-45" />
    </div>
    
    <div class="max-w-screen-xl mx-auto px-4 py-16 relative z-10">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
        <!-- Brand Column -->
        <div>
          <div class="mb-4">
            <div class="inline-block h-1 w-10 bg-blue-600 mb-2" />
            <span class="text-sm uppercase tracking-wider text-gray-500 font-medium">I Am</span>
          </div>
          <h3 class="text-2xl font-bold text-gray-900 mb-4">
            <span class="text-blue-600">Winnode</span>
          </h3>
          <p class="text-gray-600 mb-4">
            Providing reliable validator nodes and blockchain infrastructure solutions.
          </p>
        </div>
        
        <!-- Links Column 1 -->
        <div>
          <h3 class="text-lg font-bold text-gray-900 mb-5">
            Company
          </h3>
          <ul class="space-y-3">
            <li>
              <a
                href="/about"
                class="text-gray-600 hover:text-blue-600 transition-colors"
              >About Us</a>
            </li>
            <li>
              <a
                href="/mainnet"
                class="text-gray-600 hover:text-blue-600 transition-colors"
              >Mainnet</a>
            </li>
            <li>
              <a
                href="/testnet"
                class="text-gray-600 hover:text-blue-600 transition-colors"
              >Testnet</a>
            </li>
            <li>
              <a
                href="/contact"
                class="text-gray-600 hover:text-blue-600 transition-colors"
              >Contact</a>
            </li>
          </ul>
        </div>
        
        <!-- Links Column 2 -->
        <div>
          <h3 class="text-lg font-bold text-gray-900 mb-5">
            Resources
          </h3>
          <ul class="space-y-3">
            <li>
              <a
                href="/privacy-policy"
                class="text-gray-600 hover:text-blue-600 transition-colors"
              >Privacy Policy</a>
            </li>
            <li>
              <a
                href="/licensing"
                class="text-gray-600 hover:text-blue-600 transition-colors"
              >Licency</a>
            </li>
          </ul>
        </div>
        
        <!-- Newsletter Column -->
        <div>
          <h3 class="text-lg font-bold text-gray-900 mb-5">
            Stay Connected
          </h3>
          <p class="text-gray-600 mb-4">
            Subscribe to our newsletter for the latest updates.
          </p>
          <div class="flex">
            <input 
              type="email" 
              placeholder="Your email" 
              class="px-4 py-2 w-full border border-gray-300 rounded-l-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
            >
            <button class="px-4 py-2 bg-blue-600 text-white rounded-r-full hover:bg-blue-700 transition-colors">
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <div class="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-gray-100">
        <!-- Copyright -->
        <div class="text-gray-600 text-sm mb-4 md:mb-0">
          © 2025 <a
            href="https://winnode.xyz/"
            class="hover:text-blue-600 transition-colors"
          >Winnode</a>. All Rights Reserved.
        </div>
        
        <!-- Social Links -->
        <div class="flex space-x-4">
          <!-- Telegram -->
          <a
            href="https://t.me/Winnodexx"
            class="text-gray-500 hover:text-blue-600 transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.05-.2-.06-.06-.17-.04-.25-.02-.11.02-1.85 1.17-5.21 3.46-.49.33-.94.5-1.35.48-.44-.02-1.3-.25-1.93-.46-.78-.26-1.39-.4-1.34-.85.03-.22.17-.44.48-.67 1.88-1.25 3.94-2.58 5.03-3.29 1.7-1.11 3.47-2.23 4.24-2.69.79-.47 1.4-.88 2.15-.84.26.01.6.13.79.5.03.07.05.53-.07 1.2z" />
            </svg>
          </a>
  
          <!-- Twitter -->
          <a
            href="https://x.com/WinNode"
            class="text-gray-500 hover:text-blue-600 transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
            </svg>
          </a>
  
          <!-- GitHub -->
          <a
            href="https://github.com/winnode"
            class="text-gray-500 hover:text-blue-600 transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                clip-rule="evenodd"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>